/**
 * The How to play pages' pictures (docs/spec/04 §6): the real sprites acting each idea out in a
 * small window. Each is a loop driven by the page's clock; nothing here is the game's own state.
 */

import { BLUE, CYAN, SKY, WHITE, YELLOW } from '../../render/palette';
import type { Screen } from '../../render/screen';
import { drawSprite, UP } from '../../render/sprites';
import type { SpriteId } from '../../render/sprites';
import type { Rect } from '../kit/menuModel';
import { ACCENT, calmStep, FAINT, MUTED, SELECTED, wingFrame } from '../kit/theme';
import type { HelpPageId } from './helpPages';
import { hiveRows } from './helpPages';

const DOWN = Math.PI / 2;
const TARGETS = ['stinger', 'moth', 'monarch'] as const;

/** Draw inside `r` only, so a sprite flying off the picture is cut at its frame. */
function clipped(screen: Screen, r: Rect, fn: () => void): void {
  const ctx = screen.ctx;
  ctx.save();
  ctx.beginPath();
  ctx.rect(r.x + 1, r.y + 1, r.w - 2, r.h - 2);
  ctx.clip();
  fn();
  ctx.restore();
}

/** Shots rising from (x, y): one every `every` ms, each climbing `reach` pixels. `xAt` is where it left from. */
function shots(screen: Screen, ms: number, y: number, reach: number, every: number, xAt: (t: number) => number): void {
  const life = reach / 0.16;
  for (let k = Math.floor(ms / every); k >= 0 && ms - k * every < life; k--) {
    const born = k * every;
    drawSprite(screen, 'shot', 0, xAt(born), y - (ms - born) * 0.16);
  }
}

/** Where the picture's Lancer is at `t`: gliding from side to side. */
function glide(r: Rect, t: number, reach: number): number {
  return Math.round(r.x + r.w / 2 + Math.sin(t / 800) * reach);
}

function controlsArt(screen: Screen, r: Rect, ms: number): void {
  const cx = r.x + r.w / 2;
  const frame = wingFrame(ms);
  TARGETS.forEach((id, i) => drawSprite(screen, id, frame, cx + (i - 1) * 48, r.y + 16));
  const y = r.y + r.h - 16;
  const x = glide(r, ms, 56);
  shots(screen, ms, y - 10, 40, 420, (t) => glide(r, t, 56));
  drawSprite(screen, 'lancer', 0, x, y);
  // Which way it is going, as the stick would show.
  const east = Math.cos(ms / 800) >= 0;
  screen.text('◄', x - 22, y - 4, east ? FAINT : SELECTED);
  screen.text('►', x + 14, y - 4, east ? SELECTED : FAINT);
}

function hiveArt(screen: Screen, r: Rect, ms: number): void {
  const cell = r.w / 3;
  hiveRows().forEach((row, i) => {
    const cx = Math.round(r.x + cell * (i + 0.5));
    // The Monarch shows both its coats: green, and the violet of its second life.
    const struck = row.sprite === 'monarch' && Math.floor(ms / 1600) % 2 === 1;
    drawSprite(screen, struck ? 'monarchHit' : row.sprite, wingFrame(ms), cx, r.y + 24);
    screen.text(row.name, cx, r.y + 44, ACCENT, 'center');
    screen.text(`${row.home}/${row.diving}`, cx, r.y + 58, SELECTED, 'center');
  });
}

/** The tractor beam: a cone of running stripes from (cx, top), `len` long. */
function beam(screen: Screen, cx: number, top: number, len: number, ms: number): void {
  const colours = [BLUE, SKY, CYAN, WHITE, CYAN, SKY];
  const run = Math.floor(ms / calmStep(70));
  for (let i = 0; i < len; i += 2) {
    const half = Math.round(3 + (i / len) * 12);
    const colour = colours[(((Math.floor(i / 4) - run) % colours.length) + colours.length) % colours.length] ?? BLUE;
    screen.fill(cx - half, top + i, half * 2, 1, colour);
  }
}

function captureArt(screen: Screen, r: Rect, ms: number): void {
  const cell = r.w / 3;
  const at = (i: number): number => Math.round(r.x + cell * (i + 0.5));
  const frame = wingFrame(ms);
  for (let i = 0; i < 3; i++) {
    screen.text(String(i + 1), r.x + 6 + Math.round(cell * i), r.y + 6, ACCENT);
    if (i > 0) screen.fill(Math.round(r.x + cell * i), r.y + 4, 1, r.h - 8, FAINT);
  }
  // 1: the beam, and a ship rising up it, turning as it goes.
  const rise = (ms % 2400) / 2400;
  drawSprite(screen, 'monarch', frame, at(0), r.y + 16, DOWN);
  beam(screen, at(0), r.y + 26, 46, ms);
  drawSprite(screen, 'lancer', 0, at(0), r.y + 66 - rise * 30, UP + Math.floor(rise * 12) * (Math.PI / 6));
  // 2: the Monarch diving with its captive behind it, and the shot that frees it.
  const dive = Math.sin(ms / 500) * 6;
  drawSprite(screen, 'captive', 0, at(1) - dive, r.y + 12, DOWN);
  drawSprite(screen, 'monarch', frame, at(1) + dive, r.y + 30, DOWN);
  shots(screen, ms, r.y + 56, 16, 700, () => at(1));
  drawSprite(screen, 'lancer', 0, at(1), r.y + 68);
  // 3: the dual fighter and its double volley.
  for (const side of [-8, 8]) {
    shots(screen, ms, r.y + 56, 44, 500, () => at(2) + side);
    drawSprite(screen, 'lancer', 0, at(2) + side, r.y + 68);
  }
}

function challengeArt(screen: Screen, r: Rect, ms: number): void {
  const span = r.w + 48;
  const mid = r.y + 30;
  for (let i = 0; i < 8; i++) {
    // A file of eight weaving across, nose to tail, and never firing.
    const x = r.x - 24 + ((((ms * 0.07 - i * 20) % span) + span) % span);
    const y = mid + Math.sin(x / 22) * 16;
    const heading = Math.atan2((16 / 22) * Math.cos(x / 22), 1);
    drawSprite(screen, i % 2 === 0 ? 'stinger' : 'moth', wingFrame(ms), x, y, heading);
  }
  const y = r.y + r.h - 12;
  shots(screen, ms, y - 10, 20, 300, (t) => glide(r, t, 40));
  drawSprite(screen, 'lancer', 0, glide(r, ms, 40), y);
}

function novaArt(screen: Screen, r: Rect, ms: number): void {
  const cx = r.x + r.w / 2;
  const angry: SpriteId = Math.floor(ms / 4000) % 2 === 1 ? 'empressAngry' : 'empress';
  drawSprite(screen, angry, wingFrame(ms), Math.round(cx + Math.sin(ms / 900) * 44), r.y + 26);
  const y = r.y + r.h - 12;
  drawSprite(screen, 'lancer', 0, cx, y);
  // The Nova: a ring opening from the ship every few seconds.
  const age = ms % 3000;
  if (age < 700) {
    const radius = 6 + age * 0.06;
    const colour = age < 250 ? WHITE : age < 500 ? CYAN : SKY;
    for (let k = 0; k < 20; k++) {
      const a = (k / 20) * Math.PI * 2;
      screen.fill(cx + Math.cos(a) * radius, y + Math.sin(a) * radius * 0.6, 2, 2, colour);
    }
  }
  // The chain climbing through its multipliers, in the corner clear of her swing.
  const level = 1 + (Math.floor(ms / 900) % 5);
  const my = r.y + r.h - 14;
  screen.text(`X${level}`, r.x + 6, my, YELLOW);
  for (let i = 0; i < 5; i++) {
    if (i < level) screen.fill(r.x + 26 + i * 5, my + 1, 4, 6, YELLOW);
    else screen.box(r.x + 26 + i * 5, my + 1, 4, 6, MUTED);
  }
}

const ART: Readonly<Partial<Record<HelpPageId, (screen: Screen, r: Rect, ms: number) => void>>> = {
  controls: controlsArt,
  hive: hiveArt,
  capture: captureArt,
  challenge: challengeArt,
  nova: novaArt,
};

/** Draw page `id`'s picture inside `r` at `ms` into the page (the capsules page has none: it is a list). */
export function drawHelpArt(screen: Screen, id: HelpPageId, r: Rect, ms: number): void {
  const art = ART[id];
  if (art) clipped(screen, r, () => art(screen, r, ms));
}
